import {
  type ActivityLine,
  describeActivity,
  LINKED_PRODUCTS,
  type LinkedProduct,
  type LinkStatus,
  missingExtraScopes,
  PRODUCT_NAMES,
} from "@rhema/client";
import { Button, Card, Eyebrow, ProductMark, Sheet } from "@rhema/ui";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import type { LinkedState } from "./LinkCallback";
import { LinkForm } from "./LinkForm";
import { MARK, useLinkOverview } from "./useLinks";

/** The button that asks for a product's extra scopes, for links made before they existed. */
const ALLOW_MORE: Partial<Record<LinkedProduct, string>> = {
  queravel: "Allow Queravel to use your documents, memory and connected apps",
};

/** What Rhema can do with each app, in one line. */
const PURPOSE: Record<LinkedProduct, string> = {
  queravel: "Ask questions about a chat. Queravel keeps nothing.",
  malakh: "Save messages and photos as an email draft.",
  penielly: "Send photos from your library and save photos you receive.",
};

function when(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusText(s: LinkStatus): string {
  if (!s.available) return "Not available right now";
  if (s.linked) return s.linked_at ? `Linked since ${when(s.linked_at)}` : "Linked";
  return "Not linked";
}

function Activity({ version }: { version: number }) {
  const { links } = useLinkOverview();
  const [lines, setLines] = useState<ActivityLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reload when a link changes
  useEffect(() => {
    links.api
      .activity()
      .then((rows) => setLines(describeActivity(rows).slice(0, 20)))
      .catch((e: Error) => setError(e.message));
  }, [links, version]);

  return (
    <Card className="px-4 py-3.5">
      <h3 className="m-0 text-[15px] font-bold">Activity</h3>
      <p className="m-0 mt-0.5 text-[12.5px] text-ink-secondary">
        What Rhema sent to your other apps from this account.
      </p>
      {error && <p className="m-0 mt-2 text-sm text-scarlet">{error}</p>}
      {lines && lines.length === 0 && <p className="m-0 mt-2 text-sm text-ink-meta">Nothing yet.</p>}
      {lines && lines.length > 0 && (
        <ul aria-label="Connected app activity" className="m-0 mt-1.5 list-none p-0">
          {lines.map((line) => (
            <li
              key={`${line.at}-${line.text}`}
              className="flex items-center gap-3 border-t border-line py-2.5 first:border-t-0"
            >
              <ProductMark product={MARK[line.product]} size={26} />
              <span className="min-w-0 flex-1 text-sm">{line.text}</span>
              <span className="text-[12.5px] text-ink-meta">{when(line.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Settings section for Queravel, Malakh and Penielly. Hidden when the server has it switched off. */
export function ConnectedApps() {
  const { overview, error, reload, links } = useLinkOverview();
  const [linking, setLinking] = useState<LinkedProduct | null>(null);
  // Linking again to allow scopes a link made earlier does not have.
  const [widening, setWidening] = useState<LinkedProduct | null>(null);
  const [unlinking, setUnlinking] = useState<LinkedProduct | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  // A link that finished on /link/callback comes back here with the product in the route state.
  const arrived = (useLocation().state as LinkedState | null)?.linked;
  const [linked, setLinked] = useState<LinkedProduct | null>(
    LINKED_PRODUCTS.includes(arrived as LinkedProduct) ? (arrived as LinkedProduct) : null,
  );

  const changed = useCallback(async () => {
    await reload();
    setVersion((v) => v + 1);
  }, [reload]);

  if (!overview?.enabled) {
    return error ? (
      <Card className="px-4 py-3.5">
        <p className="m-0 text-sm text-scarlet">{error}</p>
      </Card>
    ) : null;
  }

  const unlink = async (product: LinkedProduct) => {
    setBusy(true);
    setProblem(null);
    try {
      await links.api.unlink(product);
      setUnlinking(null);
      setLinked(null);
      await changed();
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const statuses = LINKED_PRODUCTS.map((p) => overview.products.find((s) => s.product === p)).filter(
    (s): s is LinkStatus => Boolean(s),
  );

  return (
    <section aria-labelledby="connected-apps-title" id="connected-apps" className="flex flex-col gap-3">
      <Eyebrow className="px-1">
        <h2 id="connected-apps-title" className="m-0 text-xs font-bold">
          Connected apps
        </h2>
      </Eyebrow>
      <p className="m-0 px-1 text-[13px] text-ink-secondary">
        Rhema only sends what you choose, and asks before anything is written.
      </p>
      {linked && (
        <p role="status" className="m-0 px-1 text-sm font-semibold text-olive-ink">
          Your {PRODUCT_NAMES[linked]} account is linked.
        </p>
      )}
      <ul aria-label="Apps you can link" className="m-0 flex list-none flex-col gap-2 p-0">
        {statuses.map((s) => {
          const name = PRODUCT_NAMES[s.product];
          return (
            <li key={s.product} className="rounded-card border border-line bg-surface px-4 py-3.5">
              <div className="flex items-center gap-3">
                <ProductMark product={MARK[s.product]} size={36} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-[15px] font-semibold">{name}</span>
                  <span className="text-[12.5px] text-ink-secondary">{PURPOSE[s.product]}</span>
                  <span
                    className={
                      s.linked ? "text-[12.5px] font-semibold text-olive-ink" : "text-[12.5px] text-ink-meta"
                    }
                  >
                    {statusText(s)}
                  </span>
                </span>
                {s.linked ? (
                  <Button variant="danger" size="sm" onClick={() => setUnlinking(s.product)}>
                    Unlink
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="tonal"
                    disabled={!s.available}
                    onClick={() => {
                      setLinked(null);
                      setLinking(s.product);
                    }}
                    aria-label={`Link ${name}`}
                  >
                    Link
                  </Button>
                )}
              </div>
              {s.linked && ALLOW_MORE[s.product] && missingExtraScopes(s.product, s.scopes).length > 0 && (
                <div className="mt-3 flex flex-col items-start gap-2 border-t border-line pt-3">
                  <p className="m-0 text-[12.5px] text-ink-secondary">
                    {name} can answer from your own documents, memory and connected apps once you allow it.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setLinked(null);
                      setWidening(s.product);
                    }}
                  >
                    {ALLOW_MORE[s.product]}
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <Activity version={version} />

      <Sheet
        open={linking !== null}
        onClose={() => setLinking(null)}
        fixed={false}
        title={linking ? `Link ${PRODUCT_NAMES[linking]}` : ""}
        className="px-5 pb-7"
      >
        {linking && (
          <div className="mt-3">
            <LinkForm
              product={linking}
              onDone={() => {
                setLinked(linking);
                setLinking(null);
                setVersion((v) => v + 1);
              }}
              onCancel={() => setLinking(null)}
            />
          </div>
        )}
      </Sheet>

      <Sheet
        open={widening !== null}
        onClose={() => setWidening(null)}
        fixed={false}
        title={widening ? (ALLOW_MORE[widening] ?? "") : ""}
        className="px-5 pb-7"
      >
        {widening && (
          <div className="mt-3">
            <LinkForm
              product={widening}
              wants={missingExtraScopes(widening, [])}
              onDone={() => {
                setLinked(widening);
                setWidening(null);
                void changed();
              }}
              onCancel={() => setWidening(null)}
            />
          </div>
        )}
      </Sheet>

      <Sheet
        open={unlinking !== null}
        onClose={() => setUnlinking(null)}
        fixed={false}
        title={unlinking ? `Unlink ${PRODUCT_NAMES[unlinking]}?` : ""}
        className="px-5 pb-7"
      >
        {unlinking && (
          <>
            <p className="m-0 mt-3 text-[15px] leading-normal text-ink-secondary">
              Rhema will stop reaching {PRODUCT_NAMES[unlinking]} for you. Nothing in either app is deleted.
            </p>
            {problem && (
              <p role="alert" className="m-0 mt-2 text-sm font-semibold text-scarlet">
                {problem}
              </p>
            )}
            <div className="mt-5 flex flex-col gap-2">
              <Button variant="danger-fill" size="lg" disabled={busy} onClick={() => void unlink(unlinking)}>
                {busy ? "Unlinking" : `Unlink ${PRODUCT_NAMES[unlinking]}`}
              </Button>
              <Button variant="ghost" onClick={() => setUnlinking(null)}>
                Keep it linked
              </Button>
            </div>
          </>
        )}
      </Sheet>
    </section>
  );
}
